import {ChangeEvent, FC, Fragment, MouseEvent, ReactNode, useContext, useEffect, useRef, useState} from 'react';
import {useRecoilValue, useSetRecoilState} from 'recoil';
import {useFormContext} from 'react-hook-form';
import {toast} from 'react-toastify';
import {get, omit} from 'lodash';

import {Box, Button, IconButton, Stack, Tooltip, Typography} from '@mui/material';
import UploadIcon from '@mui/icons-material/Upload';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';

import {EventBusContext} from '../../../../providers/EventBus';

import {useFileUpload} from '../../../../hooks/useFileUpload';

import {IDocument} from '../../../../shared/types/document.types';
import {ISelectedDocToReplace} from './utils';

import {DocumentService} from '../../../../api/services/document.service';

import {colors} from '../../../../shared/colors';

import {
  contentTreeQueryTrigger,
  docsToReplaceState,
  filesFormNameMapState,
  serviceRequestActiveTabState,
} from '../../../../store/service-requests/atoms';
import {ServiceRequestService} from '../../../../api/services/serviceRequest.service';
import {useParams} from 'react-router-dom';
import {ModalContainer} from '../../../modals/ModalContainer';
import {ConfirmAction} from '../../../modals/ConfirmAction';
import {DateHelpers} from '../../../../shared/helpers/dateHelpers';
import {InputTypeEnum} from '../../../../shared/types/workContent.types';
import {WorkDataInformationEdit} from './WorkDataInformationEdit';
import {useCompanyTariffGuard, useRefreshCompanyTariff} from '../../../../modules/company-tariff';

interface IWorkDataFiles {
  item: {
    files?: IDocument[];
    text: string;
    formName: string;
    mandatory?: boolean;
    maxFilesAllowed?: number;
    availableFileExtensions?: string;
  };
  isEditable: boolean;
  isTextVisible?: boolean;
  isTextEmptyVisible?: boolean;
  isMultipleInput?: boolean;
}

const getMoreThanAllowedText = (maxFiles: number) =>
  `Количество выбранных/загруженных файлов больше допустимого значения. Максимальное значение: ${maxFiles}`;

const ACCEPT = '.docx,.docx,.ppt,.pptx,.xls,.xlsx,.pdf';

const isFile = (mimeType: string) => {
  if (!mimeType) {
    return false;
  }

  const generalType = mimeType.split('/')[0].toUpperCase();

  return [InputTypeEnum.VIDEO, InputTypeEnum.AUDIO, InputTypeEnum.IMAGE].every((i) => i !== generalType);
};

export const WorkDataFiles: FC<IWorkDataFiles> = ({
  item,
  isEditable,
  isTextVisible = true,
  isTextEmptyVisible = true,
  isMultipleInput = false,
}) => {
  const prevFiles = item?.files?.length ? item.files.filter((i) => isFile(i.mimeType)) : [];
  const {id} = useParams();
  const activeTab = useRecoilValue(serviceRequestActiveTabState);
  const {checkTariff, tariffLoading} = useCompanyTariffGuard();
  const refreshCompanyTariff = useRefreshCompanyTariff();
  const inputRef = useRef<HTMLInputElement>(null);
  const {files, fileNames, setFiles, clearAllFiles, removeFile} = useFileUpload();
  const {
    files: filesToReplace,
    fileNames: fileNamesToReplace,
    setFiles: setFilesToReplace,
    clearAllFiles: clearAllFilesToReplace,
  } = useFileUpload();
  const setFilesFormNameMap = useSetRecoilState(filesFormNameMapState);
  const {bus} = useContext(EventBusContext);
  const [selectedToReplace, setSelectedToReplace] = useState<ISelectedDocToReplace | null>(null);
  const [replaceMode, setReplaceMode] = useState(false);
  const setDocsToReplace = useSetRecoilState(docsToReplaceState);
  const [open, setOpen] = useState(false);
  const [idToDelete, setIdToDelete] = useState<string | null>(null);
  const triggerContentTreeQuery = useSetRecoilState(contentTreeQueryTrigger);
  const isOnlyUploadMode = Boolean(activeTab?.onlyUpload);
  const isEditMode = isEditable && activeTab?.editMode;
  const canModifyExistingFiles = isEditMode && !isOnlyUploadMode;

  const handleFileSelect = () => {
    if (!checkTariff(true)) return;

    inputRef.current?.click();
  };

  const closeModal = () => {
    setOpen(false);
    setIdToDelete(null);
  };

  const deleteHandler = async (setLoading: (arg: boolean) => void) => {
    if (!idToDelete || !id) {
      return;
    }

    try {
      setLoading(true);
      await ServiceRequestService.deleteDocument(Number(id), idToDelete);
      refreshCompanyTariff();
      triggerContentTreeQuery((v) => v + 1);
      closeModal();
    } catch (err) {
      bus?.emit('error', [err]);
    } finally {
      setLoading(false);
    }
  };

  const isFilesExist = prevFiles.length;

  const toggleSelected = (url: string, index: number) => {
    if (!prevFiles || !prevFiles?.length || !canModifyExistingFiles) {
      return;
    }

    setSelectedToReplace((prev) =>
      prev?.oldSrc === url ? prev : {oldSrc: url, newSrc: null, docId: prevFiles[index].id},
    );
  };

  const {setValue, getValues} = useFormContext();

  const defaultValueRef = useRef(getValues(item.formName));

  const onDownLoad = async (e: MouseEvent<HTMLSpanElement>, doc: IDocument) => {
    try {
      e.preventDefault();

      const res = await DocumentService.getContent(doc.id);

      const url = URL.createObjectURL(res.data);

      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = doc.fileName;
      anchor.style.display = 'none';
      document.body.append(anchor);
      anchor.click();
      anchor.remove();
    } catch (err) {
      bus?.emit('error', [err]);
    }
  };

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.currentTarget?.files && !replaceMode) {
      const filesFromEvent = e.currentTarget?.files || [];
      const prevFiles = getValues(item.formName) as string | File[];

      const filesArr = Array.from(filesFromEvent);
      const downloadedFilesLength = item?.files?.length ?? 0;
      const formFilesLength = isMultipleInput && Array.isArray(prevFiles) ? prevFiles.length : 0;

      const isMoreThanAllowed = item.maxFilesAllowed
        ? filesArr.length > item.maxFilesAllowed ||
          filesArr.length + downloadedFilesLength > item.maxFilesAllowed ||
          filesArr.length + downloadedFilesLength + formFilesLength > item.maxFilesAllowed
        : false;

      if (isMoreThanAllowed) {
        toast.error(getMoreThanAllowedText(item.maxFilesAllowed as number), {containerId: 'default'});

        return;
      }
    }

    if (replaceMode) {
      if (selectedToReplace && e.currentTarget?.files?.length) {
        setSelectedToReplace((prev) =>
          prev ? {...prev, newSrc: URL.createObjectURL((e.currentTarget?.files as FileList)[0])} : prev,
        );
      }

      setFilesToReplace(e);
    } else {
      setFiles(e);
    }
  };

  const changeInputFileList = (fileName: string) => {
    if (!inputRef.current) return;
    const fileList = inputRef.current.files || [];
    const dt = new DataTransfer();

    for (let i = 0; i < fileList.length; i++) {
      if (fileList[i].name !== fileName) {
        dt.items.add(fileList[i]);
      }
    }
    inputRef.current.files = dt.files;
  };

  const onClearToReplace = () => {
    if (!inputRef.current) return;
    const dt = new DataTransfer();
    inputRef.current.files = dt.files;
  };

  const onRemove = (fileName: string) => {
    changeInputFileList(fileName);
    removeFile(fileName);
  };

  useEffect(() => {
    if (!canModifyExistingFiles && replaceMode) {
      setSelectedToReplace(null);
      setReplaceMode(false);
      clearAllFilesToReplace();
      if (inputRef.current) {
        const dt = new DataTransfer();
        inputRef.current.files = dt.files;
      }
    }
  }, [canModifyExistingFiles, replaceMode, clearAllFilesToReplace]);

  const renderNonEditForm = () =>
    prevFiles.length ? (
      prevFiles.map((file) => (
        <Box key={file.id}>
          <Tooltip
            placement="bottom-start"
            title={
              <Fragment>
                <Typography sx={{fontSize: '12px'}} textOverflow="ellipsis">
                  <b>Дата создания:</b> {DateHelpers.formatToRu(get(file, `createdDate`))}
                </Typography>
                <Typography sx={{fontSize: '12px'}} textOverflow="ellipsis">
                  <b>Автор:</b> {get(file, `createdFio`) || '-'}
                </Typography>
                <Typography sx={{fontSize: '12px'}} textOverflow="ellipsis">
                  <b>Комментарий:</b> {get(file, `description`) || '-'}
                </Typography>
              </Fragment>
            }
          >
            <Typography
              onClick={(e) => onDownLoad(e, file)}
              color="blue"
              sx={{cursor: 'pointer'}}
              variant="subtitle2"
              noWrap
              textOverflow="ellipsis"
            >
              {file.fileName}
            </Typography>
          </Tooltip>
        </Box>
      ))
    ) : isTextEmptyVisible ? (
      <Typography variant="subtitle2">Не загружено ни одного файла</Typography>
    ) : null;

  const renderEditForm = () => {
    let arrUploadedFiles: ReactNode[] = [];
    let arrLocalFiles: ReactNode[] = [];

    if (files.length) {
      arrLocalFiles = arrLocalFiles.concat(
        files.map((file, ind) => {
          return (
            <Stack key={file.name + file.lastModified} direction="row" spacing={1}>
              <Box>
                <Tooltip title={fileNames[ind]}>
                  <Typography color="blue" variant="subtitle2" noWrap>
                    {fileNames[ind]}
                  </Typography>
                </Tooltip>
              </Box>
              <Stack direction="row" gap={2}>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemove(file.name);
                  }}
                  sx={{width: 20, height: 20}}
                >
                  <HighlightOffIcon />
                </IconButton>
                {canModifyExistingFiles && <WorkDataInformationEdit document={prevFiles[ind]} />}
              </Stack>
            </Stack>
          );
        }),
      );
    }

    if (prevFiles) {
      arrUploadedFiles = arrUploadedFiles.concat(
        prevFiles.map((file, ind) => {
          const selected = selectedToReplace?.oldSrc === (file.link || file.path);

          const fileName = selected && fileNamesToReplace[0] ? fileNamesToReplace[0] : file.fileName;

          return (
            <Stack
              direction="row"
              key={file.id}
              spacing={1}
              sx={{
                borderRadius: '6px',
                border: selected ? `2px solid blue` : undefined,
              }}
            >
              <Box
                onClick={() => {
                  if (replaceMode) {
                    toggleSelected((file.link || file.path) as string, ind);
                  }
                }}
              >
                <Typography
                  onClick={(e) => !replaceMode && onDownLoad(e, file)}
                  color="blue"
                  sx={{cursor: 'pointer'}}
                  variant="subtitle2"
                  noWrap
                >
                  {fileName}
                </Typography>
              </Box>
              {canModifyExistingFiles && (
                <Stack direction="row" gap={2}>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      setIdToDelete(prevFiles?.[ind]?.id as string);
                      setOpen(true);
                    }}
                    sx={{width: 20, height: 20}}
                  >
                    <HighlightOffIcon />
                  </IconButton>
                  <WorkDataInformationEdit document={file} />
                </Stack>
              )}
            </Stack>
          );
        }),
      );
    }

    const showSplash = !arrUploadedFiles.length && !arrLocalFiles.length;

    return (
      <>
        {showSplash && isTextEmptyVisible && <Typography variant="subtitle2">Не загружено ни одного файла</Typography>}
        {arrUploadedFiles}
        {arrLocalFiles}
      </>
    );
  };

  useEffect(() => {
    if (selectedToReplace && filesToReplace.length) {
      setDocsToReplace((prev) =>
        prev
          .filter((i) => i.docId !== selectedToReplace.docId)
          .concat({...omit(selectedToReplace, ['newSrc']), file: filesToReplace[0]}),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedToReplace?.newSrc]);

  useEffect(() => {
    if (selectedToReplace?.oldSrc) {
      clearAllFilesToReplace();
      onClearToReplace();
      setDocsToReplace((prev) => prev.filter((i) => i.docId !== selectedToReplace.docId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedToReplace?.oldSrc]);

  useEffect(() => {
    setFilesFormNameMap((prev) => {
      prev.set(item.formName, true);

      return prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!activeTab?.editMode) {
      clearAllFiles();
      clearAllFilesToReplace();
      onClearToReplace();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab?.editMode]);

  useEffect(() => {
    const syncSelectedFilesToForm = (selectedFiles: File[], needToConcat = true) => {
      const prevFiles = getValues(item.formName) as File[];

      const newValue =
        isMultipleInput && Array.isArray(prevFiles)
          ? Array.from(
              new Set(
                prevFiles
                  .filter((i) => !selectedFiles.find((file) => file.name === i.name))
                  .concat(needToConcat ? selectedFiles : []),
              ),
            )
          : files.length
            ? files
            : defaultValueRef.current;

      setValue(item.formName, newValue, {shouldDirty: true});
    };

    syncSelectedFilesToForm(files);

    return () => {
      syncSelectedFilesToForm(files, false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);

  return (
    <Stack gap={1.5} sx={{flexWrap: 'wrap'}} direction="column">
      <Stack direction="row">
        {item.mandatory ? (
          <Typography variant="subtitle2" color="red">
            *
          </Typography>
        ) : null}
        {isTextVisible && (
          <Typography
            sx={{color: colors['icons--primary'], width: 'fit-content', maxWidth: activeTab?.editMode ? 290 : 320}}
            textOverflow="ellipsis"
            noWrap
            variant="subtitle2"
          >
            {item.text}
          </Typography>
        )}
      </Stack>
      <Stack direction="column" sx={{width: '100%'}}>
        {!isEditMode ? renderNonEditForm() : renderEditForm()}
      </Stack>
      {isEditMode ? (
        <Stack spacing={1} direction="row">
          {!replaceMode && (
            <Tooltip title="Выбрать файлы для загрузки">
              <IconButton
                onClick={handleFileSelect}
                disabled={tariffLoading}
                aria-label="Загрузить документы"
                size="small"
              >
                <UploadIcon />
              </IconButton>
            </Tooltip>
          )}
          {canModifyExistingFiles && isFilesExist ? (
            <Stack spacing={1} direction="row">
              <Button
                onClick={() => {
                  if (!replaceMode) {
                    setReplaceMode(true);
                  } else {
                    handleFileSelect();
                  }
                }}
                disabled={tariffLoading || (replaceMode && !selectedToReplace)}
              >
                {!replaceMode ? 'Замена файла' : 'Выбрать'}
              </Button>
              {replaceMode && (
                <Button
                  onClick={() => {
                    setSelectedToReplace(null);
                    setReplaceMode(false);
                    clearAllFilesToReplace();
                    onClearToReplace();
                  }}
                >
                  Отмена
                </Button>
              )}
            </Stack>
          ) : null}
        </Stack>
      ) : null}
      {isEditMode ? (
        <input
          type="file"
          ref={inputRef}
          style={{display: 'none'}}
          onChange={onChange}
          multiple={!replaceMode}
          accept={ACCEPT}
        />
      ) : null}
      {open && (
        <ModalContainer
          sideEffect={deleteHandler}
          renderModal={(sideEffect, loading) => (
            <ConfirmAction
              loading={loading}
              open={open}
              handleClose={closeModal}
              handleSideEffect={sideEffect}
              title="Вы уверены? После данного действия файл будет удален навсегда"
              buttonTitle="Удалить"
            />
          )}
        />
      )}
    </Stack>
  );
};
